import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as CodeBrowserIcon } from "../../assets/icons/code-browser.svg";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { fetchProcessedModelSchemas } from "../../store/processedModelSchemaSlice";
import { getMyOrganizations } from "../../store/userSlice";

import { useProcessedModelLimit } from "../../hooks/useProcessedModelLimit";

import type { AppDispatch, RootState } from "../../store";

interface ModelSchema {
  id: string;
  name: string;
  type?: string;
  isActive?: boolean;
}

interface ProcessedModelSchemasSliceState {
  schemas: Record<string, ModelSchema>;
  loading: string;
}

interface UserSliceState {
  organizations: { loaded?: boolean } | null;
}

const ModelsWidget: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { schemas, loading: schemasLoading } = useSelector((state: RootState) => state.processedModelSchemas as unknown as ProcessedModelSchemasSliceState);
  const { organizations } = useSelector((state: RootState) => state.user as unknown as UserSliceState);
  const { evaluate: evaluateModelLimit } = useProcessedModelLimit();

  useEffect(() => {
    if (schemasLoading === "idle") dispatch(fetchProcessedModelSchemas());
    if (!organizations?.loaded) dispatch(getMyOrganizations());
  }, [dispatch, schemasLoading, organizations?.loaded]);

  const modelSchemas = Object.values(schemas || {}) as ModelSchema[];

  const isLoadingSchemas = (schemasLoading === "idle" || schemasLoading === "pending") && modelSchemas.length === 0;

  const handleAddModel = () => {
    // Admins are exempt; everyone else is capped by their subscription tier (same check as the chat deploy card).
    const check = evaluateModelLimit();
    if (check && !check.allowed) {
      dispatch(setModalState({
        modalOpen: true,
        modalView: modalViewsConst.modelLimitReached,
        properties: {
          __modalSize: "xs",
          tier: check.tier,
          limit: check.limit,
          currentCount: check.currentCount,
          source: "projects_models_widget",
        },
      }));
      return;
    }

    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.modelGarden,
      properties: {},
    }));
  };

  return (
    <div className="border border-gray-200 rounded-md overflow-hidden flex flex-col h-[17.5rem] lg:h-auto lg:flex-1 lg:min-h-0">

      {/* ── Model Schemas section ── */}
      <div className="flex-none flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <CodeBrowserIcon className="w-4 h-4 text-eai-primary-accent" />
          Model Schemas
        </div>
        <button onClick={handleAddModel} className="eai-btn-grn text-sm">
          + New Model
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoadingSchemas ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-eai-primary-accent border-t-transparent" />
          </div>
        ) : modelSchemas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 gap-2">
            <p className="text-sm text-gray-400">No model schemas yet</p>
            <button onClick={handleAddModel} className="eai-btn-grn text-sm">
              + New Model
            </button>
          </div>
        ) : (
          <ul className="p-2 flex flex-col gap-1">
            {modelSchemas.map((model) => (
              <li key={model.id} className="flex items-center gap-3 px-2 py-2 mx-2 rounded-md border border-transparent">
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${model.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-700"}`}>
                  {model.isActive ? "Active" : "Inactive"}
                </span>
                <span className="text-sm font-medium text-gray-900 truncate flex-1">{model.name}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">{model.type}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

    </div>
  );
};

export default ModelsWidget;
